export interface PaymentFailureMessage {
	message: string;
	description?: string;
}

/**
 * Stripe error types that describe how we called Stripe rather than anything
 * the customer did. Their prose names integration internals — `payment_method_types`,
 * `allowed_payment_method_types` — so it must never be passed through.
 */
const INTEGRATION_FAULT_TYPES = [
	'api_error',
	'authentication_error',
	'invalid_request_error',
];

/**
 * Integration faults arrive two ways, and only one of them is a `StripeError`:
 * Stripe throws an `IntegrationError` when it rejects the call before sending
 * it, and returns a typed error when its API rejects it.
 */
export const isStripeIntegrationFault = (error: unknown): boolean => {
	if (!error || typeof error !== 'object') {
		return false;
	}

	const { name, type } = error as { name?: unknown; type?: unknown };

	return (
		name === 'IntegrationError' ||
		(typeof type === 'string' && INTEGRATION_FAULT_TYPES.includes(type))
	);
};

/**
 * Stripe's *card* errors are already written for customers and pass through.
 * Field-level validation errors are excluded deliberately — those belong inside
 * the Element, and mirroring them would show the same problem twice — and
 * integration faults are replaced, since their prose is written for us.
 *
 * The copy is the caller's: saving a card and taking a payment fail differently
 * enough that the same sentence cannot serve both, so only the reading of the
 * error is shared.
 */
export const getStripeErrorMessage = (
	// `name` alongside `type` because a thrown `IntegrationError` carries only the
	// former, and it reaches this the same way a returned `StripeError` does
	error: { name?: string; type?: string; message?: string },
	copy: {
		/** shown for a fault of ours, which must never read as a decline */
		integrationFault: PaymentFailureMessage;
		/** shown when Stripe refused without prose of its own */
		generic: PaymentFailureMessage;
	},
): PaymentFailureMessage | undefined => {
	if (error.type === 'validation_error') {
		return undefined;
	}

	if (isStripeIntegrationFault(error)) {
		return copy.integrationFault;
	}

	return error.message ? { message: error.message } : copy.generic;
};
